import { useEffect, useState, useCallback } from 'react';
import { Infinity } from 'lucide-react';
import googlePay from '../../assets/logo/Google_Pay.png';
import applePay from '../../assets/logo/Apple_Pay.png';
import binance from '../../assets/logo/Binance_Logo.svg.png';
import bancontact from '../../assets/logo/Bancontact.png';
import mastercard from '../../assets/logo/Mastercard.png';
import visa from '../../assets/logo/Visa.png';
import stadiumImg from '../../assets/wc2026/stadium.png';
import {
  WC_TARGET,
  wcPlans,
  wcLifetime,
  wcPerks,
} from './wcPlans';

const pad = (n) => String(n).padStart(2, '0');

function getCountdown() {
  const diff = WC_TARGET - Date.now();
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0 };
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff / 3600000) % 24),
    minutes: Math.floor((diff / 60000) % 60),
    seconds: Math.floor((diff / 1000) % 60),
  };
}

function CountdownBlock({ timeLeft }) {
  const items = [
    { v: timeLeft.days, l: 'Días' },
    { v: pad(timeLeft.hours), l: 'Horas' },
    { v: pad(timeLeft.minutes), l: 'Min' },
    { v: pad(timeLeft.seconds), l: 'Seg' },
  ];
  return (
    <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
      {items.map((item, i) => (
        <div
          key={item.l}
          className={`w-[72px] sm:w-20 h-[76px] sm:h-[88px] rounded-2xl bg-white/5 border border-amber-400/25 flex flex-col items-center justify-center backdrop-blur-md ${i === 3 ? 'animate-pulse' : ''}`}
        >
          <span className="text-2xl sm:text-3xl font-black text-white tabular-nums">{item.v}</span>
          <span className="text-[10px] uppercase tracking-wider text-amber-200/60 mt-1">{item.l}</span>
        </div>
      ))}
    </div>
  );
}

export default function Wc() {
  const [timeLeft, setTimeLeft] = useState(getCountdown);
  const [spots, setSpots] = useState(23);

  useEffect(() => {
    const tick = setInterval(() => setTimeLeft(getCountdown()), 1000);
    return () => clearInterval(tick);
  }, []);

  useEffect(() => {
    const spotTimer = setInterval(() => {
      setSpots((s) => (s > 8 ? s - 1 : s));
    }, 45000);
    return () => clearInterval(spotTimer);
  }, []);

  const scrollToOffers = useCallback(() => {
    document.getElementById('ofertas-mundial')?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const paymentLogos = [
    { src: googlePay, alt: 'Google Pay' },
    { src: applePay, alt: 'Apple Pay' },
    { src: visa, alt: 'Visa' },
    { src: mastercard, alt: 'Mastercard' },
    { src: bancontact, alt: 'Bancontact' },
    { src: binance, alt: 'Binance' },
  ];

  return (
    <main className="relative min-h-screen bg-[#060B1E] text-white overflow-hidden">
      {/* Ambient */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-amber-500/15 blur-[120px]" />
        <div className="absolute top-1/3 -right-32 w-[400px] h-[400px] rounded-full bg-yellow-400/10 blur-[100px]" />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: 'radial-gradient(circle, #fbbf24 1px, transparent 1px)',
            backgroundSize: '28px 28px',
          }}
        />
      </div>

      {/* Flash bar */}
      <div className="relative z-20 bg-gradient-to-r from-amber-600 via-yellow-500 to-amber-600 text-black text-center py-2.5 px-4 text-xs sm:text-sm font-bold tracking-wide">
        <span className="inline-flex items-center gap-2 flex-wrap justify-center">
          <span className="animate-pulse">●</span>
          OFERTA EXCLUSIVA MUNDIAL 2026 — Hasta -43% · Solo quedan{' '}
          <span className="tabular-nums">{spots}</span> cupos con descuento
          <span className="animate-pulse">●</span>
        </span>
      </div>

      {/* Hero */}
      <section className="relative z-10 pt-12 pb-16 md:pt-16 md:pb-24">
        <div className="container mx-auto px-3 sm:px-5">
          <div className="relative max-w-5xl mx-auto text-center">
            <img
              src={stadiumImg}
              alt=""
              aria-hidden
              className="absolute inset-0 w-full h-full object-cover opacity-[0.08] rounded-3xl mask-image-gradient"
              style={{
                maskImage: 'radial-gradient(ellipse 80% 70% at 50% 40%, black, transparent)',
              }}
            />

            <div className="relative">
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-amber-400/40 bg-amber-400/10 text-amber-200 text-xs font-bold uppercase tracking-widest mb-6">
                <svg className="w-4 h-4 text-amber-400" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                  <path d="M5 3h14v2H5V3zm2 2h10l1 4c0 3.5-2 6.5-5 8v3h4v2H7v-2h4v-3c-3-1.5-5-4.5-5-8l1-4zm5 14a6 6 0 0 0 5-7.5l-.5-2H8.5L8 11.5A6 6 0 0 0 12 19z" />
                </svg>
                Pase IPTV · TVSOLA · Mundial FIFA 2026
              </span>

              <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-white/45 mb-3">
                No te pierdas ni un solo partido
              </p>

              <h1 className="text-4xl sm:text-5xl md:text-7xl font-black uppercase leading-[0.95] mb-4">
                Ofertas{' '}
                <span className="bg-gradient-to-r from-amber-200 via-yellow-400 to-amber-500 bg-clip-text text-transparent">
                  Mundial 2026
                </span>
              </h1>

              <p className="text-gray-300 text-base sm:text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
                Precios especiales solo en esta página. Activa tu IPTV ahora y disfruta de{' '}
                <strong className="text-amber-300">todos los partidos en 4K</strong> con activación
                instantánea.
              </p>

              <div className="mb-8">
                <p className="text-[11px] uppercase tracking-widest text-amber-300/70 mb-4 font-semibold">
                  La cuenta atrás ha empezado
                </p>
                <CountdownBlock timeLeft={timeLeft} />
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <button
                  type="button"
                  onClick={scrollToOffers}
                  className="bg-yellow-400 hover:bg-yellow-300 text-black font-bold px-8 py-4 rounded-full transition-all duration-300 hover:scale-105 shadow-lg shadow-amber-500/25 w-full sm:w-auto"
                >
                  Ver ofertas con descuento
                </button>
                <a
                  href="https://wa.me/32460207833?text=Hola,%20quiero%20la%20oferta%20Mundial%202026"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border border-white/30 hover:border-amber-400/50 text-white font-semibold px-8 py-4 rounded-full transition-all w-full sm:w-auto text-center"
                >
                  Consultar por WhatsApp
                </a>
              </div>

              <p className="mt-6 text-[11px] text-white/30 uppercase tracking-wider">
                Pago único · Sin permanencia · IBO Pro gratis
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Perks */}
      <section className="relative z-10 pb-12">
        <div className="container mx-auto px-3 sm:px-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 max-w-5xl mx-auto">
            {wcPerks.map((p) => (
              <div
                key={p.title}
                className="rounded-2xl bg-white/5 border border-amber-400/15 p-5 text-center backdrop-blur-sm hover:border-amber-400/35 transition-colors"
              >
                <p className="text-xl font-black text-amber-400 mb-1">{p.title}</p>
                <p className="text-sm text-gray-400">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="ofertas-mundial" className="relative z-10 py-16 scroll-mt-24">
        <div className="container mx-auto px-3 sm:px-5">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 rounded-full bg-red-500/20 border border-red-400/40 text-red-300 text-xs font-bold uppercase mb-4">
              Precios bloqueados hoy
            </span>
            <h2 className="text-3xl md:text-5xl font-bold mb-4">
              Elige tu pase{' '}
              <span className="text-yellow-400">Mundial</span>
            </h2>
            <p className="text-gray-400 max-w-xl mx-auto">
              Los precios tachados son el valor habitual. En esta campaña pagas menos — mismo
              servicio premium TVSOLA.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto items-stretch">
            {wcPlans.map((plan) => (
              <div key={plan.id} className="relative group h-full">
                <div
                  className={`h-full flex flex-col rounded-3xl p-8 pt-10 border backdrop-blur-md transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl ${
                    plan.isPopular
                      ? 'bg-gradient-to-br from-yellow-400/15 to-amber-600/10 border-yellow-400/40 ring-1 ring-yellow-400/30'
                      : 'bg-white/5 border-white/10 hover:border-amber-400/25'
                  }`}
                >
                  {plan.badge && (
                    <div className="absolute -top-0 right-4 z-10">
                      <span
                        className={`inline-block text-xs font-black uppercase px-4 py-1.5 rounded-b-xl ${
                          plan.isPopular
                            ? 'bg-yellow-400 text-black'
                            : 'bg-amber-600/80 text-white'
                        }`}
                      >
                        {plan.badge}
                      </span>
                    </div>
                  )}

                  <div className="absolute top-4 left-4">
                    <span className="bg-red-500 text-white text-[10px] font-black px-2 py-1 rounded-md">
                      -{plan.savePercent}%
                    </span>
                  </div>

                  <div className="mb-6 mt-4">
                    <h3 className="text-2xl font-black text-white mb-1">{plan.name}</h3>
                    <p className="text-amber-300/90 text-sm font-medium">{plan.highlight}</p>
                  </div>

                  <div className="mb-6">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="text-lg text-gray-500 line-through decoration-red-400/80">
                        {plan.originalPrice}€
                      </span>
                      <span className="text-4xl font-black text-yellow-400">{plan.price}€</span>
                    </div>
                    <p className="text-gray-400 text-sm mt-1">/ {plan.duration}</p>
                    <p className="text-green-400 text-xs font-semibold mt-2">
                      Ahorras {(parseFloat(plan.originalPrice) - parseFloat(plan.price)).toFixed(0)}€
                    </p>
                  </div>

                  {plan.screens && (
                    <p className="text-yellow-400 text-sm font-semibold mb-4">
                      {plan.screens} pantallas activas
                    </p>
                  )}

                  <div className="flex-grow space-y-3 mb-8">
                    {plan.features.map((f) => (
                      <div key={f} className="flex gap-3 text-sm text-gray-300">
                        <svg className="w-5 h-5 text-yellow-400 shrink-0" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19l12-12l-1.41-1.41z" />
                        </svg>
                        <span>{f}</span>
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    onClick={() => { window.location.href = plan.link; }}
                    className={`w-full font-bold py-4 rounded-full transition-all hover:scale-105 ${
                      plan.isPopular
                        ? 'bg-yellow-400 hover:bg-yellow-300 text-black shadow-lg'
                        : 'bg-white/10 hover:bg-amber-400/20 border border-white/20 text-white'
                    }`}
                  >
                    Aprovechar oferta
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Lifetime VIP */}
          <div className="mt-14 flex justify-center">
            <div className="relative w-full max-w-lg group">
              <div className="absolute -inset-1 bg-gradient-to-r from-yellow-400 via-amber-500 to-orange-500 rounded-3xl blur opacity-40 group-hover:opacity-60 transition" />
              <div className="relative rounded-3xl bg-gradient-to-br from-yellow-400/20 via-amber-900/30 to-[#060B1E] border-2 border-yellow-400/50 p-8 backdrop-blur-md">
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <span className="bg-gradient-to-r from-yellow-400 to-orange-500 text-black text-xs font-black uppercase px-5 py-2 rounded-full flex items-center gap-1 shadow-lg">
                    <Infinity className="w-4 h-4" />
                    {wcLifetime.badge}
                    <Infinity className="w-4 h-4" />
                  </span>
                </div>

                <div className="text-center mt-6 mb-6">
                  <h3 className="text-2xl font-black">{wcLifetime.name}</h3>
                  <p className="text-gray-400 text-sm mt-1">Un pago · Para siempre</p>
                </div>

                <div className="text-center mb-6">
                  <span className="text-xl text-gray-500 line-through decoration-red-400 mr-2">
                    {wcLifetime.originalPrice}€
                  </span>
                  <span className="text-5xl font-black text-yellow-400">{wcLifetime.price}€</span>
                  <span className="block text-red-400 text-sm font-bold mt-2">
                    -{wcLifetime.savePercent}% · Ahorras 120€
                  </span>
                </div>

                <ul className="space-y-2 mb-8 text-sm text-gray-300">
                  {wcLifetime.features.map((f) => (
                    <li key={f} className="flex gap-2">
                      <span className="text-yellow-400">✓</span> {f}
                    </li>
                  ))}
                </ul>

                <button
                  type="button"
                  onClick={() => { window.location.href = wcLifetime.link; }}
                  className="w-full bg-gradient-to-r from-yellow-400 to-orange-500 hover:from-yellow-300 hover:to-orange-400 text-black font-black py-4 rounded-full transition-all hover:scale-105"
                >
                  Obtener acceso de por vida
                </button>
              </div>
            </div>
          </div>

          {/* Trust strip */}
          <div className="mt-12 flex flex-wrap justify-center gap-6 text-center text-sm text-gray-500">
            <span>✓ Activación instantánea</span>
            <span>✓ +79.000 canales</span>
            <span>✓ Soporte WhatsApp 24/7</span>
            <span>✓ Garantía de reembolso</span>
          </div>
        </div>
      </section>

      {/* Payments */}
      <section className="relative z-10 py-16 border-t border-white/5">
        <div className="container mx-auto px-3 sm:px-5">
          <h3 className="text-2xl font-bold text-center mb-8">
            Pago <span className="text-yellow-400">seguro</span>
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:flex md:flex-wrap md:justify-center gap-4">
            {paymentLogos.map((logo) => (
              <div
                key={logo.alt}
                className="flex items-center justify-center p-4 bg-white/5 rounded-2xl border border-white/10 hover:border-yellow-400/30 transition-all md:w-32"
              >
                <img src={logo.src} alt={logo.alt} className="h-8 object-contain" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WhatsApp CTA */}
      <section className="relative z-10 pb-24">
        <div className="container mx-auto px-3 sm:px-5">
          <div className="max-w-3xl mx-auto rounded-3xl bg-gradient-to-r from-yellow-400/10 to-amber-600/5 border border-yellow-400/30 p-8 md:p-10 text-center">
            <h3 className="text-2xl md:text-3xl font-bold mb-3">
              ¿Dudas sobre la <span className="text-yellow-400">oferta Mundial</span>?
            </h3>
            <p className="text-gray-300 mb-6">
              Escríbenos por WhatsApp y te ayudamos a elegir el mejor pase antes de que suban los
              precios.
            </p>
            <a
              href="https://wa.me/32460207833?text=Hola,%20quiero%20la%20oferta%20Mundial%202026"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-300 text-black font-bold px-8 py-4 rounded-full transition-all hover:scale-105"
            >
              Hablar por WhatsApp
            </a>
          </div>
        </div>
      </section>
    </main>
  );
}
